import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Crown, MapPin, Calendar, Clock, CheckCircle2 } from "lucide-react";

const DAYS = [
    { v: 0, l: "Sun" }, { v: 1, l: "Mon" }, { v: 2, l: "Tue" },
    { v: 3, l: "Wed" }, { v: 4, l: "Thu" }, { v: 5, l: "Fri" }, { v: 6, l: "Sat" },
];

const CITIES = ["Kingston", "Portmore", "Spanish Town", "Montego Bay", "Ocho Rios", "Mandeville"];

export default function VIPOnboardingPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sub, setSub] = useState(null);
    const [form, setForm] = useState({
        address: "",
        city: "Kingston",
        weekly_ironing_dow: 3,
        weekly_wash_fold_dow: 5,
        monthly_deep_dom: 1,
        monthly_regular_dom: 15,
        time_slot: "10:00",
    });

    useEffect(() => {
        api.get("/vip/me/subscription")
            .then(({ data }) => {
                if (!data.active) {
                    toast.error("You need an active VIP membership first.");
                    navigate("/vip");
                    return;
                }
                setSub(data);
                if (data.schedule) setForm({ ...form, ...data.schedule });
                setLoading(false);
            })
            .catch(() => {
                toast.error("Could not load your subscription.");
                navigate("/vip");
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async () => {
        if (!form.address.trim()) {
            toast.error("Please enter your service address.");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/vip/me/schedule", form);
            toast.success("Schedule saved · we'll auto-book from tomorrow.");
            navigate("/my-bookings");
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <div className="min-h-screen bg-background" data-testid="vip-onboarding-page">
            <Header />

            <section className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <div className="flex items-center gap-3 mb-4">
                    <Crown size={20} strokeWidth={1.5} className="gold-text" />
                    <div className="overline">Membership setup</div>
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter">
                    Set your <span className="italic gold-grad">Royal schedule.</span>
                </h1>
                <p className="mt-4 text-muted-foreground max-w-xl leading-relaxed">
                    Tell us where and when to show up. We'll auto-book your covered deep clean, regular clean,
                    ironing pickup and wash & fold on the days you choose — no more manual bookings.
                </p>

                <div className="mt-10 space-y-8">
                    {/* ADDRESS */}
                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <MapPin size={14} className="gold-text" strokeWidth={1.5} />
                            Service address
                        </Label>
                        <Input
                            value={form.address}
                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                            placeholder="e.g. 12 Hope Rd, Apt 4B"
                            className="bg-transparent border-white/15 h-12"
                            data-testid="wizard-address"
                        />
                        <div className="grid grid-cols-3 gap-2 mt-3">
                            {CITIES.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setForm({ ...form, city: c })}
                                    className={`text-xs uppercase tracking-[0.15em] py-2 border rounded-sm transition-colors ${
                                        form.city === c
                                            ? "border-primary bg-primary/10 gold-text"
                                            : "border-white/10 text-muted-foreground hover:border-white/30"
                                    }`}
                                    data-testid={`wizard-city-${c.toLowerCase().replace(" ", "-")}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* WEEKLY */}
                    <DayPicker
                        icon={<Calendar size={14} className="gold-text" strokeWidth={1.5} />}
                        label="Ironing pickup day"
                        value={form.weekly_ironing_dow}
                        onChange={(v) => setForm({ ...form, weekly_ironing_dow: v })}
                        testid="wizard-ironing-day"
                    />
                    <DayPicker
                        icon={<Calendar size={14} className="gold-text" strokeWidth={1.5} />}
                        label="Wash & fold pickup day"
                        value={form.weekly_wash_fold_dow}
                        onChange={(v) => setForm({ ...form, weekly_wash_fold_dow: v })}
                        testid="wizard-wash-day"
                    />

                    {/* MONTHLY */}
                    <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                            <Label className="mb-2 block">Deep clean day of month</Label>
                            <Input
                                type="number"
                                min={1}
                                max={28}
                                value={form.monthly_deep_dom}
                                onChange={(e) => setForm({ ...form, monthly_deep_dom: parseInt(e.target.value) || 1 })}
                                className="bg-transparent border-white/15 h-12"
                                data-testid="wizard-deep-dom"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">Regular clean day of month</Label>
                            <Input
                                type="number"
                                min={1}
                                max={28}
                                value={form.monthly_regular_dom}
                                onChange={(e) => setForm({ ...form, monthly_regular_dom: parseInt(e.target.value) || 15 })}
                                className="bg-transparent border-white/15 h-12"
                                data-testid="wizard-regular-dom"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <Clock size={14} className="gold-text" strokeWidth={1.5} />
                            Preferred time slot
                        </Label>
                        <Input
                            value={form.time_slot}
                            onChange={(e) => setForm({ ...form, time_slot: e.target.value })}
                            placeholder="10:00"
                            className="bg-transparent border-white/15 h-12"
                            data-testid="wizard-time"
                        />
                    </div>

                    <Button
                        onClick={save}
                        disabled={saving}
                        className="w-full h-12 bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                        data-testid="wizard-save-btn"
                    >
                        {saving ? "Saving..." : "Lock in my Royal schedule"}
                        <CheckCircle2 size={14} strokeWidth={1.5} className="ml-2" />
                    </Button>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function DayPicker({ icon, label, value, onChange, testid }) {
    return (
        <div>
            <Label className="flex items-center gap-2 mb-2">{icon}{label}</Label>
            <div className="grid grid-cols-7 gap-1">
                {DAYS.map((d) => (
                    <button
                        key={d.v}
                        type="button"
                        onClick={() => onChange(d.v)}
                        className={`text-xs uppercase tracking-[0.15em] py-3 border rounded-sm transition-colors ${
                            value === d.v
                                ? "border-primary bg-primary/10 gold-text"
                                : "border-white/10 text-muted-foreground hover:border-white/30"
                        }`}
                        data-testid={`${testid}-${d.v}`}
                    >
                        {d.l}
                    </button>
                ))}
            </div>
        </div>
    );
}
